/**
 * DataTable — sortable columns, pagination, row selection, empty state, loading skeleton
 *
 * Supports two pagination modes:
 *  - Client-side (default): DataTable handles paging internally from the full `data` array.
 *  - Server-side: pass `serverPagination=true` with `currentPage`, `totalItems`,
 *    `onPageChange`. DataTable renders data as-is and delegates page changes to the caller.
 *
 * @example Client-side (all data at once)
 * <DataTable columns={cols} data={allRows} pageSize={20} />
 *
 * @example Server-side (backend paginated)
 * <DataTable
 *   columns={cols}
 *   data={pageRows}
 *   pageSize={20}
 *   serverPagination
 *   currentPage={page}           // 1-indexed
 *   totalItems={total}
 *   onPageChange={(p) => setPage(p)}
 * />
 */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import EmptyState from './EmptyState';
import Pagination from './Pagination';

// ─── Sort icon helper ────────────────────────────────────────────────────────
function SortIcon({ direction }) {
  if (direction === 'asc') return <ChevronUp size={14} className="ml-1 inline-block" />;
  if (direction === 'desc') return <ChevronDown size={14} className="ml-1 inline-block" />;
  return <ChevronsUpDown size={14} className="ml-1 inline-block opacity-40" />;
}

// ─── Loading skeleton rows ────────────────────────────────────────────────────
function SkeletonRows({ columns, rows = 5, selectable }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {selectable && (
        <TableCell className="w-10">
          <Skeleton className="h-4 w-4 rounded" />
        </TableCell>
      )}
      {columns.map((col) => (
        <TableCell key={col.key}>
          <Skeleton className="h-4 rounded" style={{ width: col.skeletonWidth || '80%' }} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

// ─── DataTable ────────────────────────────────────────────────────────────────
export default function DataTable({
  columns,
  data: dataProp,
  loading,
  selectable,
  onSelectionChange,
  pageSize: pageSizeProp,
  rowKey,
  className,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  // Server-side pagination props
  serverPagination,
  currentPage,
  totalItems,
  onPageChange,
}) {
  const { t } = useLanguage();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [internalPage, setInternalPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Avoid creating a new [] on every render when dataProp is undefined:
  // pass dataProp directly to useMemo so the stable parent reference is used.
  const pageSize = pageSizeProp || 20;

  // ── Sorting (client-side only) ─────────────────────────────────────────────
  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
    if (!serverPagination) setInternalPage(1);
  };

  const sorted = useMemo(() => {
    const data = dataProp || [];
    // In server-side mode don't re-sort; backend already sorted
    if (serverPagination || !sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [dataProp, sortKey, sortDir, serverPagination]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  // Server-side: use external page/total; don't slice data (already a single page)
  // Client-side: slice sorted data internally
  const activePage     = serverPagination ? (currentPage || 1) : internalPage;
  const effectiveTotal = serverPagination ? (totalItems ?? (dataProp?.length ?? 0)) : sorted.length;
  const totalPages     = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const visibleRows    = serverPagination
    ? sorted                                                    // backend already paged
    : sorted.slice((activePage - 1) * pageSize, activePage * pageSize);

  const handlePageChange = (p) => {
    if (serverPagination) {
      onPageChange?.(p);
    } else {
      setInternalPage(p);
    }
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const getId = (row) => {
    if (typeof rowKey === 'function') return rowKey(row);
    if (rowKey) return row[rowKey];
    return row.id ?? row._id;
  };
  const allPageIds = visibleRows.map(getId);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));
  const someSelected = allPageIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) {
      allPageIds.forEach((id) => next.delete(id));
    } else {
      allPageIds.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
    onSelectionChange?.(Array.from(next));
  };

  const toggleRow = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    onSelectionChange?.(Array.from(next));
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Table */}
      <div className="rounded-xl border-2 border-[var(--border-light)] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[var(--bg-app)] hover:bg-[var(--bg-app)]">
              {selectable && (
                <TableHead scope="col" className="w-10">
                  <Checkbox
                    checked={allSelected}
                    data-state={someSelected ? 'indeterminate' : undefined}
                    onCheckedChange={toggleAll}
                    aria-label={t('admin.select_all')}
                  />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  scope="col"
                  className={cn(
                    'font-bold text-[var(--text-main)] text-xs uppercase tracking-wider',
                    col.sortable && 'cursor-pointer select-none hover:text-[var(--brand-primary)]',
                    col.className,
                  )}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => handleSort(col)}
                  aria-sort={
                    sortKey === col.key
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : col.sortable
                      ? 'none'
                      : undefined
                  }
                >
                  {col.label}
                  {col.sortable && (
                    <SortIcon direction={sortKey === col.key ? sortDir : null} />
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <SkeletonRows columns={columns} selectable={selectable} />
            ) : visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="p-0"
                >
                  <EmptyState
                    icon={emptyIcon}
                    title={emptyTitle || t('admin.empty_table_title')}
                    description={emptyDescription || t('admin.empty_table_desc')}
                    className="py-12"
                  />
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                const id = getId(row);
                const isSelected = selectedIds.has(id);
                return (
                  <TableRow
                    key={id ?? Math.random()}
                    data-state={isSelected ? 'selected' : undefined}
                    className={cn(isSelected && 'bg-blue-50')}
                  >
                    {selectable && (
                      <TableCell className="w-10">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(id)}
                          aria-label={t('admin.select_row')}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.cellClassName}>
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Pagination
          page={activePage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showInfo
          from={(activePage - 1) * pageSize + 1}
          to={Math.min(activePage * pageSize, effectiveTotal)}
          total={effectiveTotal}
          loading={loading}
        />
      )}
    </div>
  );
}

DataTable.propTypes = {
  /** Column definitions */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      sortable: PropTypes.bool,
      /** render(cellValue, fullRow) */
      render: PropTypes.func,
      width: PropTypes.string,
      className: PropTypes.string,
      cellClassName: PropTypes.string,
      skeletonWidth: PropTypes.string,
    }),
  ).isRequired,
  /** Row data array (full set for client-side, single page for server-side) */
  data: PropTypes.array,
  /** Show loading skeleton */
  loading: PropTypes.bool,
  /** Enable row selection checkboxes */
  selectable: PropTypes.bool,
  /** Called with array of selected ids */
  onSelectionChange: PropTypes.func,
  /** Rows per page */
  pageSize: PropTypes.number,
  /** Row key field name or function(row) => id */
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  className: PropTypes.string,
  emptyIcon: PropTypes.node,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  // ── Server-side pagination ───────────────────────────────────────────────
  /** Enable server-side pagination mode */
  serverPagination: PropTypes.bool,
  /** Current page, 1-indexed (server-side mode) */
  currentPage: PropTypes.number,
  /** Total number of items across all pages */
  totalItems: PropTypes.number,
  /** Called with new 1-indexed page number */
  onPageChange: PropTypes.func,
};

DataTable.defaultProps = {
  data: [],
  loading: false,
  selectable: false,
  serverPagination: false,
};
